"use client";

/**
 * OnboardingPlatformTour — the cross-page walkthrough that runs AFTER
 * the curated replay + alert scene. Guides the user through the four
 * big surfaces they haven't seen yet:
 *
 *   /automations  →  /community  →  /blog (labelled "trades")  →  /pricing
 *
 * Each page mounts `<OnboardingPlatformTour />`. The component reads
 * the `?ptour=<step>` query param on first paint; if it matches the
 * expected step for THIS page, it fires the page's local spotlight
 * tour via `useTour().start(...)`. On completion it either:
 *
 *   (a) navigates to the next step's URL with the next query param, or
 *   (b) on the final /pricing step, just strips the query param — the
 *       "Start Free Trial" CTA on the page takes over from there.
 *
 * Why ONE component per page (not one central "tour router"):
 *   • Each page owns its own tour steps inline. Anchors live in the
 *     page markup, copy lives in the page's scene — so when a page is
 *     refactored nothing dangles.
 *   • Hot-reload DX: edit /community's copy, only /community
 *     recompiles; no central tour registry to invalidate.
 *   • Self-contained: if a page is removed, its scene just doesn't
 *     mount and the tour gracefully re-routes on the next load.
 *
 * The step param → scene mapping lives here as a single switch so all
 * copy is easy to audit in one place.
 *
 * Handoff source: the replay's "Replay Complete" state exposes a
 * "Continue the tour →" button that navigates to
 * /automations?ptour=automations. See replay-controls.tsx.
 */

import { useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTour, type TourStep } from "@/components/onboarding-tour";
import {
  PLATFORM_TOUR_QUERY_PARAM,
  PLATFORM_TOUR_STEP_AUTOMATIONS,
  PLATFORM_TOUR_STEP_COMMUNITY,
  PLATFORM_TOUR_STEP_TRADES,
  PLATFORM_TOUR_STEP_PRICING,
  buildPlatformTourUrl,
  getNextPlatformTourStep,
  readPlatformTourStep,
  type PlatformTourStep,
} from "@/lib/platform-tour-constants";

interface OnboardingPlatformTourProps {
  /** The step this page expects to run. If the URL's ?ptour= param
   *  doesn't match, the scene stays inert. Pages are strict about
   *  their own step so /automations mounting with ?ptour=pricing
   *  doesn't accidentally fire the wrong tour. */
  expectedStep: PlatformTourStep;
}

export function OnboardingPlatformTour({ expectedStep }: OnboardingPlatformTourProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { start: startTour } = useTour();
  const startedRef = useRef(false);

  // Strip ?ptour=<step> from the URL. Called on BOTH complete + skip
  // so a browser-refresh doesn't re-fire the tour, and so navigation
  // history doesn't accumulate stale tour params. router.replace
  // (not push) keeps the back button sensible.
  const stripQueryParam = () => {
    if (typeof window === "undefined") return;
    const next = new URLSearchParams(searchParams?.toString() || "");
    next.delete(PLATFORM_TOUR_QUERY_PARAM);
    const search = next.toString();
    const path = window.location.pathname + (search ? `?${search}` : "");
    router.replace(path, { scroll: false });
  };

  // Navigate to the next step's URL. If there's no next step (we're on
  // the final /pricing scene) just strip the param — the page's own
  // "Start Free Trial" CTAs take over the conversion from here.
  const advanceToNextStep = (current: PlatformTourStep) => {
    const next = getNextPlatformTourStep(current);
    if (!next) {
      stripQueryParam();
      return;
    }
    router.push(buildPlatformTourUrl(next));
  };

  useEffect(() => {
    const activeStep = readPlatformTourStep(searchParams);
    if (!activeStep || activeStep !== expectedStep) return;
    if (startedRef.current) return;
    startedRef.current = true;

    // Brief delay so the page has time to mount + paint before the
    // spotlight tries to measure its anchors. 450ms matches the
    // alert scene's grace window — enough for hero images / API
    // hydration / lucide icons to settle without feeling sluggish.
    const t = window.setTimeout(() => {
      const steps = buildStepsForScene(activeStep, {
        onReachPricingCta: () => advanceToNextStep(activeStep),
      });
      if (!steps.length) {
        stripQueryParam();
        return;
      }
      // eslint-disable-next-line no-console
      console.info("[platform-tour] scene start", { step: activeStep });
      startTour(steps, {
        onComplete: () => {
          // eslint-disable-next-line no-console
          console.info("[platform-tour] scene complete", { step: activeStep });
          advanceToNextStep(activeStep);
        },
        onSkip: () => {
          // eslint-disable-next-line no-console
          console.info("[platform-tour] scene skipped", { step: activeStep });
          // User hit Esc / X — respect the dismissal and kill the
          // whole tour chain, not just this step. Strip only.
          stripQueryParam();
        },
      });
    }, 450);

    return () => window.clearTimeout(t);
    // We deliberately run this effect only when the component mounts
    // for the matching step. Listing `searchParams` would re-fire on
    // every query-param change (including the stripQueryParam above),
    // so the one-shot ref + the tight scope here is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedStep]);

  return null;
}

// ── Scene definitions ────────────────────────────────────────────────────────

interface SceneCallbacks {
  /** Only used by the final /pricing scene — when the user clicks
   *  the "Start Free Trial" step's CTA, we delegate to this callback
   *  so the scene component can decide whether to advance the tour or
   *  just strip the param. Currently: strips only (pricing is the
   *  terminal step), but breaking it out keeps future flow changes
   *  cheap. */
  onReachPricingCta: () => void;
}

function buildStepsForScene(
  step: PlatformTourStep,
  _cb: SceneCallbacks,
): TourStep[] {
  switch (step) {
    case PLATFORM_TOUR_STEP_AUTOMATIONS:
      return [
        {
          id: "automations-hero",
          target: "[data-tour='automations-hero']",
          placement: "bottom",
          title: "Profitable strategies, built by Xynth's quant team",
          body:
            "Every automation here is **backtested, paper-traded, and validated in live markets** by our in-house quants. Pick one, subscribe, and start compounding. **Made by Xynth = proven edge.**",
        },
        {
          id: "automations-grid",
          target: "[data-tour='automations-grid']",
          placement: "top",
          title: "Subscribe to a winner",
          body:
            "Filter to **Made by Xynth** for our quant team's signature strategies, or **Made by Community** for niche plays from our top-performing users. Sort by **Most Subscribers** to see what's printing for the most traders right now.",
          cta: "Next — Community",
        },
      ];

    case PLATFORM_TOUR_STEP_COMMUNITY:
      return [
        {
          id: "community-hero",
          target: "[data-tour='community-hero']",
          placement: "bottom",
          title: "Research from the platform's top earners",
          body:
            "Every card here is a public Xynth session that produced **real, profitable trades**. Full notebook, charts, code, conclusions — the exact playbook our top users ran to put trades on.",
        },
        {
          id: "community-feed",
          target: "[data-tour='community-feed']",
          placement: "top",
          title: "Sort, filter, fork the winners",
          body:
            "**Sort** by relevance or recency. **Filter** by category, difficulty, or theme. Find the breakdown that matches your style, fork it, run the same research on your own ticker. Stop guessing — copy what's already working.",
          cta: "Next — Trade ideas",
        },
      ];

    case PLATFORM_TOUR_STEP_TRADES:
      return [
        {
          id: "trades-hero",
          target: "[data-tour='trades-hero']",
          placement: "bottom",
          title: "Proven trade ideas with proven returns",
          body:
            "From **beginner-friendly Guides** that walk you through your first profitable setup, to **advanced** options flow, IV crush, whale watching, and earnings plays our top users run weekly. **Every card shows the actual return — receipts on the table.**",
        },
        {
          id: "trades-grid",
          target: "[data-tour='trades-grid']",
          placement: "top",
          title: "Sort by Highest Return — see what's printing",
          body:
            "These aren't theoretical setups. **Every strategy here closed in the green.** Filter by Options, Earnings, Flow, Swing, or grab a Guide if you're new. New profitable ideas every week — written by traders putting their own capital behind them.",
          cta: "Next — Pricing",
        },
      ];

    case PLATFORM_TOUR_STEP_PRICING:
      return [
        {
          id: "pricing-intro",
          target: "[data-tour='pricing-pro-card']",
          placement: "left",
          title: "Pro pays for itself on a single trade",
          body:
            "Unlimited alerts, the full agent, real-time data — everything you need to **start making money on day one**. This is the tier our profitable users pick. It's not the expensive option, it's the obvious one.",
        },
        {
          id: "pricing-start-trial",
          target: "[data-tour='pricing-pro-card']",
          placement: "left",
          title: "7 days free — keep every dollar you make",
          body:
            "Build your alerts, fork the proven community strategies, ship them live. **Keep every dollar your strategies print during the trial.** Cancel anytime — but our traders don't, because it pays for itself the first week.",
          cta: "Finish tour",
        },
      ];

    default:
      return [];
  }
}
